'use client';

import { useEffect } from 'react';
import { buildDatUrl, resolveSource } from '@workspace/lib/picture.utils';
import { normalizeGalleryImage } from './useGallery';

export const useGalleryPrefetch = (
  images: unknown[] | undefined,
  imgSize?: { width: number; height: number },
): void => {
  const width = imgSize?.width ?? 640;
  const height = imgSize?.height ?? 640;

  useEffect(() => {
    if (!images?.length) return;

    const urls = [
      ...new Set(
        images
          .map((item) => normalizeGalleryImage(item as Parameters<typeof normalizeGalleryImage>[0]))
          .filter((img): img is NonNullable<typeof img> => Boolean(img))
          .flatMap((img) => {
            const resolved = resolveSource(img);
            if (!resolved.baseUrl) return [];
            if (!resolved.isBynder || resolved.isSvg) return [resolved.baseUrl];

            return [
              buildDatUrl(resolved.baseUrl, width, height),
              buildDatUrl(resolved.baseUrl, width * 2, height * 2),
              buildDatUrl(resolved.baseUrl, 100, 100),
              buildDatUrl(resolved.baseUrl, 200, 200),
            ];
          }),
      ),
    ];

    if (!urls.length) return;

    const prefetch = () => {
      urls.forEach((url) => {
        const img = new Image();
        img.decoding = 'async';
        img.src = url;
      });
    };

    if ('requestIdleCallback' in window) {
      const id = window.requestIdleCallback(prefetch);
      return () => window.cancelIdleCallback(id);
    }

    const timeout = setTimeout(prefetch, 1500);
    return () => clearTimeout(timeout);
  }, [images, width, height]);
};
